import React from 'react';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { ArrowLeft, CheckCircle, Target, TrendingUp, Zap } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
export function ImplementingAiRecommendationsPage() {
  const navigate = useNavigate();
  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/resources');
    }
  };
  return <div className="bg-white" data-id="element-1381">
      {/* Header */}
      <div className="bg-gray-900 py-12 lg:py-16" data-id="element-1382">
        <Container data-id="element-1383">
          <button onClick={handleGoBack} className="flex items-center text-gray-300 hover:text-white mb-6 transition-colors" data-id="element-1384">
            <ArrowLeft className="h-5 w-5 mr-2" data-id="element-1385" />
            Back
          </button>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-4" data-id="element-1386">
            Implementing AI Recommendations
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl" data-id="element-1387">
            Best practices for prioritizing and implementing Flowsense
            recommendations to maximize your email marketing ROI.
          </p>
        </Container>
      </div>

      {/* Content */}
      <article className="py-16 lg:py-24" data-id="element-1388">
        <Container size="md" data-id="element-1389">
          {/* Featured Image */}
          <div className="mb-12" data-id="element-1390">
            <img src="https://cdn.magicpatterns.com/uploads/2XHzzBje8ZXaRRFAwX9VfP/3._ai-recommendations.png" alt="AI Recommendations Dashboard" className="w-full rounded-lg shadow-xl" data-id="element-1391" />
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12" data-id="element-1392">
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-1393">
              Flowsense's AI analyzes your flows and generates specific,
              actionable recommendations to improve performance. This guide will
              help you understand how to prioritize, implement, and measure the
              impact of these recommendations.
            </p>
          </div>

          {/* Understanding Recommendations */}
          <section className="mb-16" data-id="element-1394">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1395">
              Understanding Recommendation Types
            </h2>
            <p className="text-lg text-gray-600 mb-6" data-id="element-1396">
              Flowsense identifies 15+ types of optimization opportunities
              across four main categories:
            </p>
            <div className="space-y-6 mb-8" data-id="element-1397">
              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500" data-id="element-1398">
                <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center" data-id="element-1399">
                  <Target className="h-5 w-5 mr-2" data-id="element-1400" />
                  Content Recommendations
                </h3>
                <ul className="space-y-2 text-blue-800" data-id="element-1401">
                  <li data-id="element-1402">• Weak or missing subject lines</li>
                  <li data-id="element-1403">• Unclear or buried CTAs</li>
                  <li data-id="element-1404">• Text-heavy emails without visual breaks</li>
                  <li data-id="element-1405">• Missing personalization opportunities</li>
                  <li data-id="element-1406">• Off-brand messaging or colors</li>
                </ul>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-500" data-id="element-1407">
                <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center" data-id="element-1408">
                  <Zap className="h-5 w-5 mr-2" data-id="element-1409" />
                  Structure Recommendations
                </h3>
                <ul className="space-y-2 text-purple-800" data-id="element-1410">
                  <li data-id="element-1411">• Suboptimal delay timing between emails</li>
                  <li data-id="element-1412">• Missing follow-up emails in sequences</li>
                  <li data-id="element-1413">• Unnecessary delays causing drop-off</li>
                  <li data-id="element-1414">• Missing conditional splits for segmentation</li>
                  <li data-id="element-1415">• Overly complex flow logic</li>
                </ul>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500" data-id="element-1416">
                <h3 className="text-lg font-semibold text-green-900 mb-3 flex items-center" data-id="element-1417">
                  <TrendingUp className="h-5 w-5 mr-2" data-id="element-1418" />
                  Timing Recommendations
                </h3>
                <ul className="space-y-2 text-green-800" data-id="element-1419">
                  <li data-id="element-1420">• Send time optimization opportunities</li>
                  <li data-id="element-1421">• Delay adjustments based on engagement patterns</li>
                  <li data-id="element-1422">• Frequency optimization to reduce unsubscribes</li>
                  <li data-id="element-1423">• Time-based segmentation suggestions</li>
                </ul>
              </div>

              <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-500" data-id="element-1424">
                <h3 className="text-lg font-semibold text-orange-900 mb-3 flex items-center" data-id="element-1425">
                  <Target className="h-5 w-5 mr-2" data-id="element-1426" />
                  Targeting Recommendations
                </h3>
                <ul className="space-y-2 text-orange-800" data-id="element-1427">
                  <li data-id="element-1428">• Audience segmentation opportunities</li>
                  <li data-id="element-1429">• Behavioral trigger refinements</li>
                  <li data-id="element-1430">• Exclusion criteria to improve relevance</li>
                  <li data-id="element-1431">• Cross-sell and upsell targeting</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Priority Levels */}
          <section className="mb-16" data-id="element-1432">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1433">
              Understanding Priority Levels
            </h2>
            <p className="text-lg text-gray-600 mb-6" data-id="element-1434">
              Every recommendation is assigned a priority level based on
              potential revenue impact and implementation effort:
            </p>
            <div className="space-y-4 mb-8" data-id="element-1435">
              <div className="flex items-start p-4 bg-red-50 border-l-4 border-red-500 rounded" data-id="element-1436">
                <div className="flex-grow" data-id="element-1437">
                  <h3 className="text-lg font-semibold text-red-900 mb-2" data-id="element-1438">
                    HIGH Priority
                  </h3>
                  <p className="text-red-700 mb-3" data-id="element-1439">
                    Critical issues with significant revenue impact. These
                    should be addressed immediately as they're likely costing
                    you money every day.
                  </p>
                  <p className="text-sm text-red-600 font-medium" data-id="element-1440">
                    Examples: Missing CTAs, broken flow logic, spammy content
                    triggering filters
                  </p>
                </div>
              </div>

              <div className="flex items-start p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded" data-id="element-1441">
                <div className="flex-grow" data-id="element-1442">
                  <h3 className="text-lg font-semibold text-yellow-900 mb-2" data-id="element-1443">
                    MEDIUM Priority
                  </h3>
                  <p className="text-yellow-700 mb-3" data-id="element-1444">
                    Meaningful improvements that will drive measurable gains.
                    Address these after resolving high-priority issues.
                  </p>
                  <p className="text-sm text-yellow-600 font-medium" data-id="element-1445">
                    Examples: Suboptimal timing, weak subject lines, missing
                    personalization
                  </p>
                </div>
              </div>

              <div className="flex items-start p-4 bg-blue-50 border-l-4 border-blue-500 rounded" data-id="element-1446">
                <div className="flex-grow" data-id="element-1447">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2" data-id="element-1448">
                    LOW Priority
                  </h3>
                  <p className="text-blue-700 mb-3" data-id="element-1449">
                    Minor optimizations and refinements. Implement these during
                    regular maintenance or A/B testing cycles.
                  </p>
                  <p className="text-sm text-blue-600 font-medium" data-id="element-1450">
                    Examples: Copy improvements, visual refinements, advanced
                    segmentation tests
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Implementation Framework */}
          <section className="mb-16" data-id="element-1451">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1452">
              Step-by-Step Implementation Framework
            </h2>

            <div className="space-y-8" data-id="element-1453">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-8 rounded-lg" data-id="element-1454">
                <div className="flex items-start mb-4" data-id="element-1455">
                  <span className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4" data-id="element-1456">
                    1
                  </span>
                  <div data-id="element-1457">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1458">
                      Review and Prioritize
                    </h3>
                    <p className="text-gray-700 mb-4" data-id="element-1459">
                      Start by reviewing all recommendations for a specific
                      flow. Sort by priority (HIGH → MEDIUM → LOW) and estimated
                      impact.
                    </p>
                    <ul className="space-y-2 text-gray-700" data-id="element-1460">
                      <li className="flex items-start" data-id="element-1461">
                        <CheckCircle className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1462" />
                        <span data-id="element-1463">Focus on high-priority items first</span>
                      </li>
                      <li className="flex items-start" data-id="element-1464">
                        <CheckCircle className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1465" />
                        <span data-id="element-1466">Consider implementation effort vs. impact</span>
                      </li>
                      <li className="flex items-start" data-id="element-1467">
                        <CheckCircle className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1468" />
                        <span data-id="element-1469">Group related recommendations together</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-8 rounded-lg" data-id="element-1470">
                <div className="flex items-start mb-4" data-id="element-1471">
                  <span className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4" data-id="element-1472">
                    2
                  </span>
                  <div data-id="element-1473">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1474">
                      Capture Baseline Metrics
                    </h3>
                    <p className="text-gray-700 mb-4" data-id="element-1475">
                      Before making changes, Flowsense automatically captures
                      your current performance metrics. This ensures you can
                      prove ROI.
                    </p>
                    <ul className="space-y-2 text-gray-700" data-id="element-1476">
                      <li className="flex items-start" data-id="element-1477">
                        <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1478" />
                        <span data-id="element-1479">Open rate, click rate, conversion rate</span>
                      </li>
                      <li className="flex items-start" data-id="element-1480">
                        <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1481" />
                        <span data-id="element-1482">Revenue per recipient</span>
                      </li>
                      <li className="flex items-start" data-id="element-1483">
                        <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1484" />
                        <span data-id="element-1485">Unsubscribe rate</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-pink-50 to-red-50 p-8 rounded-lg" data-id="element-1486">
                <div className="flex items-start mb-4" data-id="element-1487">
                  <span className="flex-shrink-0 w-10 h-10 bg-pink-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4" data-id="element-1488">
                    3
                  </span>
                  <div data-id="element-1489">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1490">
                      Implement in Klaviyo
                    </h3>
                    <p className="text-gray-700 mb-4" data-id="element-1491">
                      Use the specific suggestions provided to make changes in
                      your Klaviyo account. Flowsense provides exact copy,
                      timing, and structure recommendations.
                    </p>
                    <ul className="space-y-2 text-gray-700" data-id="element-1492">
                      <li className="flex items-start" data-id="element-1493">
                        <CheckCircle className="h-5 w-5 text-pink-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1494" />
                        <span data-id="element-1495">
                          Copy recommended subject lines and body copy
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-1496">
                        <CheckCircle className="h-5 w-5 text-pink-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1497" />
                        <span data-id="element-1498">Adjust timing and delays as suggested</span>
                      </li>
                      <li className="flex items-start" data-id="element-1499">
                        <CheckCircle className="h-5 w-5 text-pink-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1500" />
                        <span data-id="element-1501">
                          Add or modify flow steps per recommendations
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-teal-50 p-8 rounded-lg" data-id="element-1502">
                <div className="flex items-start mb-4" data-id="element-1503">
                  <span className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4" data-id="element-1504">
                    4
                  </span>
                  <div data-id="element-1505">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1506">
                      Mark as Implemented
                    </h3>
                    <p className="text-gray-700 mb-4" data-id="element-1507">
                      After making changes in Klaviyo, mark the recommendation
                      as "Implemented" in Flowsense. This starts tracking the
                      impact.
                    </p>
                    <ul className="space-y-2 text-gray-700" data-id="element-1508">
                      <li className="flex items-start" data-id="element-1509">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1510" />
                        <span data-id="element-1511">
                          Click "Mark as Implemented" in the recommendation card
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-1512">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1513" />
                        <span data-id="element-1514">Add implementation notes if needed</span>
                      </li>
                      <li className="flex items-start" data-id="element-1515">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1516" />
                        <span data-id="element-1517">
                          Flowsense begins tracking before/after metrics
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-lg" data-id="element-1518">
                <div className="flex items-start mb-4" data-id="element-1519">
                  <span className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4" data-id="element-1520">
                    5
                  </span>
                  <div data-id="element-1521">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1522">
                      Monitor Results
                    </h3>
                    <p className="text-gray-700 mb-4" data-id="element-1523">
                      Wait 7-30 days (depending on flow volume) to gather
                      statistically significant data. Flowsense automatically
                      compares before/after performance.
                    </p>
                    <ul className="space-y-2 text-gray-700" data-id="element-1524">
                      <li className="flex items-start" data-id="element-1525">
                        <CheckCircle className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1526" />
                        <span data-id="element-1527">View percentage improvements in key metrics</span>
                      </li>
                      <li className="flex items-start" data-id="element-1528">
                        <CheckCircle className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1529" />
                        <span data-id="element-1530">Calculate revenue impact of changes</span>
                      </li>
                      <li className="flex items-start" data-id="element-1531">
                        <CheckCircle className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1532" />
                        <span data-id="element-1533">Export ROI reports for stakeholders</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Best Practices */}
          <section className="mb-16" data-id="element-1534">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1535">
              Implementation Best Practices
            </h2>
            <div className="bg-gray-50 p-8 rounded-lg" data-id="element-1536">
              <ul className="space-y-4" data-id="element-1537">
                <li className="flex items-start" data-id="element-1538">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1539" />
                  <div data-id="element-1540">
                    <span className="font-semibold text-gray-900" data-id="element-1541">
                      Start with one flow at a time:
                    </span>
                    <span className="text-gray-600" data-id="element-1542">
                      {' '}
                      Focus on completely optimizing one flow before moving to
                      the next. This makes it easier to measure impact and learn
                      what works.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1543">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1544" />
                  <div data-id="element-1545">
                    <span className="font-semibold text-gray-900" data-id="element-1546">
                      Implement high-priority items first:
                    </span>
                    <span className="text-gray-600" data-id="element-1547">
                      {' '}
                      These have the biggest impact and often the quickest wins.
                      Build momentum with early successes.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1548">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1549" />
                  <div data-id="element-1550">
                    <span className="font-semibold text-gray-900" data-id="element-1551">
                      Test major changes with A/B splits:
                    </span>
                    <span className="text-gray-600" data-id="element-1552">
                      {' '}
                      For significant structural or content changes, consider
                      A/B testing to validate the recommendation before full
                      rollout.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1553">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1554" />
                  <div data-id="element-1555">
                    <span className="font-semibold text-gray-900" data-id="element-1556">
                      Document your changes:
                    </span>
                    <span className="text-gray-600" data-id="element-1557">
                      {' '}
                      Use the notes field to record what you changed and why.
                      This helps with future optimization and team
                      collaboration.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1558">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1559" />
                  <div data-id="element-1560">
                    <span className="font-semibold text-gray-900" data-id="element-1561">
                      Allow time for data collection:
                    </span>
                    <span className="text-gray-600" data-id="element-1562">
                      {' '}
                      Don't judge results too quickly. Wait for sufficient
                      volume (typically 7-30 days) before evaluating performance
                      changes.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1563">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1564" />
                  <div data-id="element-1565">
                    <span className="font-semibold text-gray-900" data-id="element-1566">
                      Reopen if needed:
                    </span>
                    <span className="text-gray-600" data-id="element-1567">
                      {' '}
                      If a change doesn't work as expected, you can reopen the
                      recommendation and try a different approach or revert the
                      change.
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </section>

          {/* Related Guides */}
          <section className="border-t border-gray-200 pt-12" data-id="element-1568">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1569">
              Related Guides
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-1570">
              <Link to="/guides/understanding-flow-health-scores" className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1571">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1572">
                  Understanding Flow Health Scores
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1573">
                  Learn how Flowsense calculates health scores and what each
                  metric means for your flows.
                </p>
                <span className="text-indigo-600 font-medium" data-id="element-1574">
                  Read Guide →
                </span>
              </Link>
              <Link to="/guides/agency-white-label-setup" className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1575">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1576">
                  Agency White-Label Setup
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1577">
                  Complete guide to setting up custom domains and branded
                  reports for your agency.
                </p>
                <span className="text-indigo-600 font-medium" data-id="element-1578">
                  Read Guide →
                </span>
              </Link>
            </div>
          </section>

          {/* CTA */}
          <div className="mt-16 bg-gray-900 rounded-2xl p-8 lg:p-12 text-center" data-id="element-1579">
            <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1580">
              Start Implementing AI Recommendations
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto" data-id="element-1581">
              Get specific, actionable recommendations for your flows and start
              improving performance today.
            </p>
            <a href="https://go.useflowsense.com/signup" data-id="element-1582">
              <Button size="lg" data-id="element-1583">Get Started Free</Button>
            </a>
          </div>
        </Container>
      </article>
    </div>;
}